import { FC, ReactNode } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { CareMapState } from '../../caremap/CareMapState';
import { ICollaboratorPermission } from '../../share-caremap/CommunityClient';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { CommunityModelFactory } from '../../share-caremap/CommunityModelFactory';
import { SharingAndAccessState } from '../../share-caremap/sharing-and-access/SharingAndAccessState';
import { LockIcon, EyeIcon, ShareCareIcon } from '../../theme/Icons';
import { PersonAvatar } from '../../avatar/Avatars';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { useNudgeStorage } from '../../utils/useNudgeStorage';
import { InfoBanner } from '../../components/InfoBanner';

const useStyles = createUseStyles({
    // Neutral state-of-sharing row (not an alert): shared-is-the-default, so a hidden note is a
    // deliberate choice, not something to warn about.
    stateRow: {
        marginTop: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'var(--ion-color-step-50)',
        borderRadius: 10,
        padding: '11px 13px'
    },
    stateLead: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        color: 'var(--ion-color-medium)'
    },
    stateText: {
        flex: 1,
        minWidth: 0,
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        lineHeight: 1.4
    },
    stateHiddenClause: {
        color: 'var(--ion-color-step-600)'
    },
    statePending: {
        color: 'var(--ion-color-step-400)'
    },
    stateAction: {
        flexShrink: 0,
        color: 'var(--ion-color-primary)',
        fontWeight: 500,
        fontSize: 14,
        whiteSpace: 'nowrap',
        cursor: 'pointer'
    },
    cluster: {
        display: 'flex'
    },
    avatarPhoto: {
        display: 'inline-flex',
        borderRadius: '50%',
        boxShadow: '0 0 0 2px #fff'
    },
    avatarOverflow: {
        boxSizing: 'border-box',
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-step-750)',
        border: '2px solid #fff'
    },
});

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;
const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export interface IAvatarPerson {
    key: string;
    name: string;
    photo?: string;
    // Authorised route for `photo`, set only when it is an uploaded care map photo.
    photoHref?: string;
    photoHasNoAuditedRoute?: IAvatarIdentity['photoHasNoAuditedRoute'];
    avatar?: string;
    color?: string;
}

export interface INoteShareHookViewProps {
    isPrivate: boolean;
    // accepted collaborators, split by role, for the "who can see this" sentence
    coOwnerCount: number;
    contributorCount: number;
    viewerCount: number;
    // a hidden note a contributor wrote stays theirs to read
    authorIsContributor: boolean;
    pendingCount: number;
    // avatars for people who can actually see this note (everyone when shared; co-owners, plus
    // the contributor who wrote it, when hidden)
    avatars: IAvatarPerson[];
    patientName: string;
    onManage: () => void;
    onInvite: () => void;
    // The empty-state invite banner (only) can be dismissed per care map; the status rows can't.
    inviteDismissed: boolean;
    onDismissInvite: () => void;
}

// Pure presentation — every state is derived from these props, so the states can be previewed
// and unit-reasoned without a live care map / community. The row's action is always "Manage"
// (people/access); a note's visibility is a note property, changed from the note menu.
export const NoteShareHookView: FC<INoteShareHookViewProps> = ({
    isPrivate, coOwnerCount, contributorCount, viewerCount, authorIsContributor, pendingCount, avatars, patientName, onManage, onInvite, inviteDismissed, onDismissInvite
}) => {
    const classes = useStyles();

    const shown = avatars.length > 4 ? avatars.slice(0, 3) : avatars;
    const overflow = avatars.length - shown.length;
    const pendingSuffix = pendingCount > 0 ? ` · ${pendingCount} pending` : '';

    const avatarCluster = (
        <div className={classes.cluster}>
            {shown.map((person, i) => (
                <div key={person.key} style={i === 0 ? undefined : { marginLeft: -8 }}>
                    <div className={`${classes.avatarPhoto} mp-mask`}>
                        <PersonAvatar
                            xsmall
                            identity={{
                                photo: person.photo,
                                avatar: person.avatar,
                                color: person.color,
                                links: person.photoHref ? { photo: { href: person.photoHref } } : undefined,
                                photoHasNoAuditedRoute: person.photoHasNoAuditedRoute
                            }}
                            name={person.name}
                        />
                    </div>
                </div>
            ))}
            {overflow > 0 && (
                <div style={{ marginLeft: -8 }}><div className={classes.avatarOverflow}>+{overflow}</div></div>
            )}
        </div>
    );

    const row = (lead: ReactNode, text: ReactNode, rowDataType: string) => (
        <div data-type="note-share-hook">
            <div className={classes.stateRow} data-type={rowDataType}>
                <div className={classes.stateLead}>{lead}</div>
                <div className={classes.stateText}>{text}</div>
                <div className={classes.stateAction} data-type="note-share-manage" onClick={onManage}>Manage</div>
            </div>
        </div>
    );

    // Hidden note: co-owners (and you) still see it, and so does the contributor who wrote it;
    // viewers and the other contributors are blocked.
    if (isPrivate) {
        const seers: string[] = [];
        if (coOwnerCount > 0) {
            seers.push(plural(coOwnerCount, 'co-owner'));
        }
        if (authorIsContributor) {
            seers.push('its author');
        }
        const canSeePart = seers.length > 0 ? capitalise(`${seers.join(' and ')} can see this`) : 'Only you can see this';
        const hiddenFromContributors = contributorCount - (authorIsContributor ? 1 : 0);
        const hiddenFrom = hiddenFromContributors > 0
            ? plural(viewerCount + hiddenFromContributors, 'other')
            : plural(viewerCount, 'viewer');
        return row(
            avatars.length > 0 ? avatarCluster : <LockIcon color="medium" size="small" />,
            <>
                {canSeePart}
                {viewerCount + hiddenFromContributors > 0 && <span className={classes.stateHiddenClause}> · hidden from {hiddenFrom}</span>}
                {pendingCount > 0 && <span className={classes.statePending}>{pendingSuffix}</span>}
            </>,
            'note-share-hidden'
        );
    }

    // Shared note with an audience: everyone with access can see it.
    if (coOwnerCount + contributorCount + viewerCount > 0) {
        const parts: string[] = [];
        if (coOwnerCount > 0) {
            parts.push(plural(coOwnerCount, 'co-owner'));
        }
        if (contributorCount > 0) {
            parts.push(plural(contributorCount, 'contributor'));
        }
        if (viewerCount > 0) {
            parts.push(plural(viewerCount, 'viewer'));
        }
        return row(
            avatarCluster,
            <>
                {parts.join(' and ')} can see this
                {pendingCount > 0 && <span className={classes.statePending}>{pendingSuffix}</span>}
            </>,
            'note-shared-with'
        );
    }

    // Shared but only invitations are out — no one has accepted yet.
    if (pendingCount > 0) {
        return row(<EyeIcon color="medium" size="small" />, `${plural(pendingCount, 'invite')} pending`, 'note-shared-with');
    }

    // Shared with no one yet — the amplified activation hook. This is the only dismissable state:
    // it's a pure nudge, and once anyone is invited it's replaced by a status row above.
    if (inviteDismissed) {
        return null;
    }
    return (
        <div data-type="note-share-hook">
            <InfoBanner
                data-type="note-share-invite"
                Icon={ShareCareIcon}
                title={<>Share {patientName}'s Care</>}
                maskTitle
                message="Invite family or care team to keep them in the loop!"
                onDismiss={onDismissInvite}
            >
                <IonButton expand="block" data-type="note-share-invite-btn" onClick={onInvite}>
                    Invite Someone
                </IonButton>
            </InfoBanner>
        </div>
    );
};

export const NoteShareHook: FC<{ note: ICaremapNoteResponse }> = ({ note }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const communityFactory = useInstance(CommunityModelFactory);
    useSubscription(() => communityFactory);

    const sharingState = useInstance(SharingAndAccessState);

    const careMap = careMapState.getActiveCaremap();

    // Only the empty-state invite banner is dismissable, and per care map (device-local). Called
    // before the early return below so the hook runs unconditionally (Rules of Hooks).
    const [inviteVisible, dismissInvite] = useNudgeStorage({ nudgeId: careMap ? `note-share-invite-${careMap.careMapId}` : undefined });

    const isViewer = careMap && !careMap.links?.self?.actions.includes('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    // The share hook is an editor affordance for managing who can see the care recipient's
    // shared notes; viewers and anonymous visitors get nothing.
    if (!careMap || isViewer || isAnonymous) {
        return null;
    }

    const community = communityFactory.forActiveCaremap();
    const coOwners = community.getPermanentCoOwners();
    const contributors = community.getPermanentContributors();
    const viewers = community.getPermanentViewers();
    const pendingCount = community.getInvites('co-owner').length + community.getInvites('contributor').length + community.getInvites('view-only').length;
    const author = contributors.find(p => p.userId === note.createdBy);

    // Avatar resolution mirrors SharingPeopleList so names/photos stay consistent with the
    // Sharing & Access sheet.
    const toAvatar = (p: ICollaboratorPermission): IAvatarPerson => {
        const link = careMapState.findMemberLinkForInvite({ email: p.invite.acceptedBy.email, accepterUserId: p.invite.acceptedBy.userId });
        const collaborator = p.collaborator;
        const name = link ? careMapState.getMemberDisplayName(link.member) : (collaborator?.name || p.invite.intendedRecipient.name || '');
        const householdMember = link?.hiveKey === 'household' ? link.member : undefined;
        return {
            key: `perm-${p.invite.token}`,
            name,
            photo: householdMember?.photo || collaborator?.pictureUrl,
            // Only set when the photo is an uploaded care map photo. A collaborator's profile
            // picture has no audited route yet.
            photoHref: householdMember?.photo ? householdMember.links?.photo?.href : undefined,
            photoHasNoAuditedRoute: householdMember?.photo ? undefined : true,
            avatar: householdMember?.avatar || collaborator?.avatar,
            color: householdMember?.color || collaborator?.color
        };
    };

    // Avatars = people who can actually see THIS note: everyone when shared; when hidden, the
    // co-owners and the contributor who wrote it (viewers and other contributors don't).
    const canSee = note.isPrivate ? [...coOwners, ...(author ? [author] : [])] : [...coOwners, ...contributors, ...viewers];

    return (
        <NoteShareHookView
            isPrivate={Boolean(note.isPrivate)}
            coOwnerCount={coOwners.length}
            contributorCount={contributors.length}
            viewerCount={viewers.length}
            authorIsContributor={Boolean(author)}
            pendingCount={pendingCount}
            avatars={canSee.map(toAvatar)}
            patientName={careMapState.getPatientFirstName()}
            onManage={() => sharingState.open('Note Share Hook', { audience: 'has-people' })}
            onInvite={() => sharingState.open('Note Share Hook', { startOnAddPerson: true, audience: 'none' })}
            inviteDismissed={!inviteVisible}
            onDismissInvite={dismissInvite}
        />
    );
};
